import type { surveyOutline } from "../../../../shared/survey-questions";
import {
  surveyQuestionNumber,
  type SurveyCondition,
} from "../../../../shared/survey-questions";
import { cn } from "@/lib/utils/cn";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VirtualSurveyQuestionList } from "./VirtualSurveyQuestionList";

const ROW_HEIGHT = 56;

export interface QuestionSummary {
  key: string;
  prompt: string;
  displayWhen?: SurveyCondition | null;
  sectionId?: string | null;
}

const getQuestionKey = (question: QuestionSummary) => question.key;

function SortableQuestionRow({
  question,
  index,
  label,
  depth,
}: {
  question: QuestionSummary;
  index: number;
  label: string;
  depth: number;
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: question.key,
    data: { parentId: question.displayWhen?.questionId ?? null },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        opacity: isDragging ? 0 : 1,
        transition,
        marginLeft: Math.min(depth, 3) * 16,
      }}
      className={cn(
        "border-border flex h-14 items-center gap-3 border-b px-1",
        isDragging && "bg-background relative z-10 shadow-sm",
      )}
    >
      <button
        type="button"
        className="text-muted-foreground bg-background hover:bg-muted flex size-8 shrink-0 cursor-grab items-center justify-center rounded-md border active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={`Reorder question ${index + 1}`}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <span className="text-muted-foreground min-w-6 shrink-0 text-sm">
        {surveyQuestionNumber(label)}
      </span>
      <span className="min-w-0 truncate text-sm font-medium">
        {question.prompt.trim() || `Untitled question ${index + 1}`}
      </span>
    </div>
  );
}

export function SurveyQuestionReorderGroup({
  questions,
  outline,
  startIndex,
  onReorder,
  scrollElement,
}: {
  questions: QuestionSummary[];
  outline: ReturnType<typeof surveyOutline>;
  startIndex: number;
  onReorder: (from: string, to: string) => void;
  scrollElement: HTMLDivElement | null;
}) {
  const groupRef = useRef<HTMLDivElement>(null);
  const keyboardTarget = useRef<string | null>(null);
  const pointerTarget = useRef<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [keyboardTargetKey, setKeyboardTargetKey] = useState<string | null>(
    null,
  );
  const [revealRequest, setRevealRequest] = useState<{ key: string }>();
  const questionByKey = new Map(
    questions.map((question) => [question.key, question]),
  );
  const activeQuestion = activeKey ? questionByKey.get(activeKey) : undefined;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      scrollBehavior: "auto",
      coordinateGetter: (event, { currentCoordinates, context }) => {
        if (event.code !== "ArrowUp" && event.code !== "ArrowDown") return;
        event.preventDefault();
        const currentKey = keyboardTarget.current ?? String(context.active?.id);
        const currentIndex = questions.findIndex(
          (question) => question.key === currentKey,
        );
        const parentId = questionByKey.get(String(context.active?.id))
          ?.displayWhen?.questionId;
        const direction = event.code === "ArrowDown" ? 1 : -1;
        for (
          let index = currentIndex + direction;
          index >= 0 && index < questions.length;
          index += direction
        ) {
          const question = questions[index];
          if (question.displayWhen?.questionId !== parentId) continue;
          keyboardTarget.current = question.key;
          setKeyboardTargetKey(question.key);
          return {
            x: currentCoordinates.x,
            y: currentCoordinates.y + (index - currentIndex) * ROW_HEIGHT,
          };
        }
      },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={(args) => {
        if (keyboardTarget.current) return [{ id: keyboardTarget.current }];
        const parentId = questionByKey.get(String(args.active.id))?.displayWhen
          ?.questionId;
        for (const collision of closestCenter(args)) {
          let target = questionByKey.get(String(collision.id));
          // A drop on a follow-up targets its ancestor at the active level.
          while (target && target.displayWhen?.questionId !== parentId) {
            target = target.displayWhen
              ? questionByKey.get(target.displayWhen.questionId)
              : undefined;
          }
          if (target) {
            pointerTarget.current = target.key;
            return [{ ...collision, id: target.key }];
          }
        }
        pointerTarget.current = null;
        return [];
      }}
      modifiers={[
        restrictToVerticalAxis,
        ({ transform, draggingNodeRect }) => {
          const bounds = groupRef.current?.getBoundingClientRect();
          if (!bounds || !draggingNodeRect) return transform;
          return {
            ...transform,
            y: Math.max(
              bounds.top - draggingNodeRect.top,
              Math.min(transform.y, bounds.bottom - draggingNodeRect.bottom),
            ),
          };
        },
      ]}
      onDragStart={({ active, activatorEvent }) => {
        setActiveKey(String(active.id));
        pointerTarget.current = null;
        keyboardTarget.current =
          activatorEvent instanceof KeyboardEvent ? String(active.id) : null;
      }}
      onDragCancel={({ active }) => {
        setActiveKey(null);
        keyboardTarget.current = null;
        pointerTarget.current = null;
        setKeyboardTargetKey(null);
        setRevealRequest({ key: String(active.id) });
      }}
      onDragEnd={({ active, over }) => {
        const target =
          keyboardTarget.current ??
          pointerTarget.current ??
          (over ? String(over.id) : null);
        setActiveKey(null);
        keyboardTarget.current = null;
        pointerTarget.current = null;
        setKeyboardTargetKey(null);
        if (target && active.id !== target)
          onReorder(String(active.id), target);
        setRevealRequest({ key: String(active.id) });
      }}
    >
      <SortableContext
        items={questions.map((question) => question.key)}
        strategy={verticalListSortingStrategy}
      >
        <div ref={groupRef} className="relative px-3">
          <VirtualSurveyQuestionList
            items={questions}
            getKey={getQuestionKey}
            scrollElement={scrollElement}
            estimateSize={ROW_HEIGHT}
            pinnedKeys={[activeKey, keyboardTargetKey].filter(
              (key): key is string => Boolean(key),
            )}
            revealRequest={revealRequest}
            label="Questions in section"
            renderItem={(question, index) => (
              <SortableQuestionRow
                question={question}
                index={startIndex + index}
                label={outline.get(question.key).label}
                depth={outline.get(question.key).depth}
              />
            )}
          />
        </div>
      </SortableContext>
      {createPortal(
        <DragOverlay dropAnimation={null} zIndex={112}>
          {activeQuestion ? (
            <div
              aria-hidden
              className="bg-background border-border flex h-14 items-center gap-3 rounded-md border px-3 shadow-lg"
            >
              <GripVertical className="text-muted-foreground size-4 shrink-0" />
              <span className="text-muted-foreground text-sm">
                {surveyQuestionNumber(outline.get(activeQuestion.key).label)}
              </span>
              <span className="truncate text-sm font-medium">
                {activeQuestion.prompt.trim() || "Untitled question"}
              </span>
            </div>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
